import React, { useState } from "react";
import { Alert, Autocomplete, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, TextField } from "@mui/material";
import { useApiCallMutation } from "../../Services/ApiCallService";
import { campaignModuleAPiEndPoints } from "../../Config/APIsEndpoints";
import { copySettingsErrors, copySettingsRequest, targetBuyerIds } from "./copySettingsLogic";

const CopyCampaignSettings = ({ open, targetCampaignIds = [], campaigns = [], onClose, onCopied }) => {
  const [copySettings, mutation] = useApiCallMutation();
  const [source, setSource] = useState(null);
  const [sections, setSections] = useState([]);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [results, setResults] = useState([]);
  const [attempted, setAttempted] = useState(false);
  const targetIds = [...new Set(targetCampaignIds)];
  const targetBuyers = targetBuyerIds(targetIds, campaigns);
  const sourceOptions = targetBuyers.length === 1 ? campaigns.filter((campaign) =>
    !targetIds.includes(campaign.campaignId) && campaign.buyerId === targetBuyers[0]) : [];
  const targetLabel = (id) => {
    const name = campaigns.find((campaign) => campaign.campaignId === id)?.campaignName;
    return name ? `${name} (${id})` : id;
  };
  const anyCopied = results.some((row) => row.ok !== false);

  const close = () => {
    if (attempted) onCopied?.();
    onClose?.();
  };

  const toggle = (section) => setSections((current) => current.includes(section)
    ? current.filter((value) => value !== section) : [...current, section]);

  const submit = async () => {
    const found = copySettingsErrors({ sourceId: source?.campaignId, targets: targetIds, sections, targetBuyers });
    setErrors(found);
    setMessage("");
    setResults([]);
    if (Object.keys(found).length) return;
    try {
      const response = await copySettings({
        endPoint: campaignModuleAPiEndPoints.copyCampaignSettings(source.campaignId),
        method: "POST",
        data: copySettingsRequest(targetIds, sections, source.campaignId),
      }).unwrap();
      setAttempted(true);
      if (response?.status?.code !== 200) {
        setMessage(typeof response?.data === "string" ? response.data : response?.data?.message || "Settings were not copied");
        return;
      }
      setResults(response?.data?.results || []);
    } catch (error) {
      setAttempted(true);
      setMessage(typeof error?.data?.data === "string" ? error.data.data : "Settings were not copied");
    }
  };

  return (
    <Dialog open={!!open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Copy settings to selected ({targetIds.length})</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ pt: 1 }}>
          <Autocomplete options={sourceOptions} value={source}
            getOptionLabel={(option) => option?.campaignName || ""}
            isOptionEqualToValue={(option, value) => option.campaignId === value.campaignId}
            onChange={(_event, value) => setSource(value)}
            renderInput={(params) => <TextField {...params} label="Source campaign"
              error={!!errors.sourceId} helperText={errors.sourceId} />} />
          <FormControlLabel control={<Checkbox checked={sections.includes("integration")} onChange={() => toggle("integration")} />}
            label="Integration" />
          <FormControlLabel control={<Checkbox checked={sections.includes("schedule")} onChange={() => toggle("schedule")} />}
            label="Schedule & caps" />
          {(errors.targets || targetBuyers.length > 1) && <Alert severity="error">{errors.targets || "Selected campaigns belong to different buyers"}</Alert>}
          {errors.sections && <Alert severity="error">{errors.sections}</Alert>}
          <Alert severity="warning">Overwrites these settings on {targetIds.length} campaigns ({targetIds.map(targetLabel).join(", ")}); nothing else changes.</Alert>
          {message && <Alert severity="error">{message}</Alert>}
          {results.map((row) => <Alert key={row.campaignId} severity={row.ok === false ? "error" : "success"}>
            {targetLabel(row.campaignId)}: {row.ok === false ? `${row.error || "Copy failed"}${row.restoreFailed && !row.error?.includes("check this campaign") ? " — check this campaign" : ""}` : [row.integration, row.schedule].filter(Boolean).join("; ")}
          </Alert>)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
        <Button onClick={submit} disabled={mutation?.isLoading || anyCopied || targetBuyers.length > 1}>Copy settings</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyCampaignSettings;
